import { PropTypes } from 'typed/imports';
import { PureComponent, ComponentEventHandler1, ReactEventHandler } from 'typed/components/utils';
import { ReactElement, ComponentElement } from 'react';
import { default as FormattedResource, Resource, resourcePropType, isResource } from '../FormattedResource/FormattedResource';
import { toArray, assertNever } from 'typed/utils';
import DropPanel, { SnapSide, CloseMode } from 'components/common/DropPanel/DropPanel';
import Spinner from 'components/common/Spinner/Spinner';
import MultiListbox, { valuePropType, LabelProps, GroupProps as MultiListboxGroupProps, OptionProps as MultiListboxOptionProps, Props as MultiListboxProps, Child as MultiListboxChild } from '../MultiListbox/MultiListbox';
import './MultiSelect.less';

export { valuePropType };

export class Label extends PureComponent<LabelProps> {
    static readonly displayName = 'MultiSelect.Label';

    static readonly propTypes = MultiListbox.Label.propTypes;

    static readonly defaultProps = MultiListbox.Label.defaultProps;

    render(): ReactElement {
        throw new Error('MultiSelect.Label can be used only inside MultiSelect');
    }
}

interface GroupProps<TValue> extends MultiListboxGroupProps<TValue> {
    children?: Children<TValue>;
}

export class Group<TValue> extends PureComponent<GroupProps<TValue>> {
    static readonly displayName = 'MultiSelect.Group';

    static readonly propTypes = MultiListbox.Group.propTypes;

    static readonly defaultProps = MultiListbox.Group.defaultProps;

    render(): ReactElement {
        throw new Error('MultiSelect.Group can be used only inside MultiSelect');
    }
}

interface OptionProps<TValue> extends MultiListboxOptionProps<TValue> {
    summary?: ReactElement | Resource;
}

export class Option<TValue> extends PureComponent<OptionProps<TValue>> {
    static readonly displayName = 'MultiSelect.Option';

    static readonly propTypes = {
        ...MultiListbox.Option.propTypes,
        summary: PropTypes.oneOfType([resourcePropType, PropTypes.element])
    };

    static readonly defaultProps = MultiListbox.Option.defaultProps;

    render(): ReactElement {
        throw new Error('MultiSelect.Option can be used only inside MultiSelect');
    }
}

type Child<TValue> =
    | undefined | null
    | ComponentElement<LabelProps, Label>
    | ComponentElement<GroupProps<TValue>, Group<TValue>>
    | ComponentElement<OptionProps<TValue>, Option<TValue>>
    | ReactElement /* TODO validate children at compile time? for some reason `<Option />` has type of Element, with no information of it's origin */;

type Children<TValue> = Child<TValue> | Child<TValue>[] | (Child<TValue> | Child<TValue>[])[];

const
    isImpossibleChild = (_child: ReactElement): _child is ReactElement => false,
    isLabel = (child: ReactElement): child is ReactElement<LabelProps> => !!child && typeof child === 'object' && child.type === Label,
    isGroup = <TValue extends any>(child: ReactElement): child is ReactElement<GroupProps<TValue>> => !!child && typeof child === 'object' && child.type === Group,
    isOption = <TValue extends any>(child: ReactElement): child is ReactElement<OptionProps<TValue>> => !!child && typeof child === 'object' && child.type === Option;

function keyify<TValue>(child: Child<TValue>, groupKey: string): { key: string, child: Child<TValue> };
function keyify<TValue>(child: Child<TValue>[], groupKey: string): { key: string, child: Child<TValue> }[];
function keyify<TValue>(child: Child<TValue> | Child<TValue>[], groupKey: string): { key: string, child: Child<TValue> } | { key: string, child: Child<TValue> }[] {
    return Array.isArray(child) ?
        child.map((subchild, subindex) => keyify(subchild, `${groupKey}.${subindex}`)) :
        {
            key: child && child.key ? `external-${child.key}` : `index-${groupKey}`,
            child
        };
}

interface Props<TValue> extends MultiListboxProps<TValue> {
    message?: ReactElement | Resource;
    placeholder?: ReactElement | Resource;
    clearable?: boolean;
    pending?: boolean;
    opened?: boolean;
    onOpen?: ComponentEventHandler1<unknown>;
    onClose?: ComponentEventHandler1<unknown>;
    children?: Children<TValue>;
};

interface State {
    opened: boolean;
}

export default class MultiSelect<TValue extends string | number | boolean> extends PureComponent<Props<TValue>, State> {
    static readonly valuePropType = valuePropType;
    static readonly Label = Label;
    static readonly Group = Group;
    static readonly Option = Option;

    static readonly propTypes = {
        ...MultiListbox.propTypes,

        message: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        placeholder: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        clearable: PropTypes.bool,
        opened: PropTypes.bool,
        onOpen: PropTypes.func,
        onChange: PropTypes.func,
        onClose: PropTypes.func
    };

    static readonly defaultProps = {
        ...MultiListbox.defaultProps,

        message: { id: 'common.items_selected' },
        placeholder: { id: 'common.items_selected' },
        clearable: true,
        opened: undefined
    }

    state: State = { opened: false };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props<TValue>) {
        if (nextProps.opened !== undefined) {
            this.setState({ opened: nextProps.opened });
        }

        if (DEBUG) {
            this._validateChildren(nextProps);
        }
    }

    private _validateChildren(props: Props<TValue> = this.props, children: Children<TValue> = props.children, usedValues: Set<TValue> = new Set<TValue>(), usedGroups: Set<TValue> = new Set<TValue>(), selectByParentID: TValue | null = null) {
        toArray(children || [])
            .flatten()
            .forEach(child => {
                if (!child || isImpossibleChild(child) || isLabel(child)) { return; }

                if (isGroup<TValue>(child)) {
                    if (!props.groupSelectionEnabled) {
                        if (usedGroups.has(child.props.id)) {
                            throw new Error(`Duplicate ID in MultiSelect.Group: ${child.props.id}`);
                        }

                        usedGroups.add(child.props.id);
                    }
                    else {
                        if (usedValues.has(child.props.id)) {
                            throw new Error(`Duplicate value in MultiSelect.Group as Option: ${child.props.id}`);
                        }

                        if (selectByParentID && props.value!.includes(child.props.id)) {
                            throw new Error(`Duplicate selection. Value ${child.props.id} included into already selected Group: ${selectByParentID}.`);
                        }

                        usedValues.add(child.props.id);
                    }

                    this._validateChildren(props, child.props.children || null, usedValues, usedGroups, selectByParentID || (props.groupSelectionEnabled && props.value!.includes(child.props.id) ? child.props.id : null));

                    return;
                }

                if (isOption<TValue>(child)) {
                    if (usedValues.has(child.props.value)) {
                        throw new Error(`Duplicate value in MultiSelect.Option: ${child.props.value}`);
                    }

                    if (selectByParentID && props.value!.includes(child.props.value)) {
                        throw new Error(`Duplicate selection. Value ${child.props.value} included into already selected Group: ${selectByParentID}.`);
                    }

                    usedValues.add(child.props.value);

                    return;
                }

                assertNever(child, 'Children of MultiSelect must be instances of MultiSelect.Label, MultiSelect.Group or MultiSelect.Option.');
            });
    }

    _onOpen: ComponentEventHandler1<unknown> = (_sender, event) => {
        this.setState({ opened: true });

        if (this.props.onOpen) {
            this.props.onOpen(this, event);
        }
    }

    _onChange: ComponentEventHandler1<TValue[]> = (_sender, value) => {
        if (this.props.onChange) {
            this.props.onChange(this, value);
        }
    }

    _onClear: ReactEventHandler = (event) => {
        if (!this.props.disabled) {
            this._onChange(this, []);
        }
        event.stopPropagation();
    }

    _onClose: ComponentEventHandler1<unknown> = (_sender, event) => {
        this.setState({ opened: false });

        if (this.props.onClose) {
            this.props.onClose(this, event);
        }
    }

    _renderChildren(children: Children<TValue>, statsToFill: { selectedCount: number } = { selectedCount: 0 }, isParentSelected: boolean = false) {
        return toArray(children || [])
            .map((child, index) => keyify(child as any as Child<TValue>[], `${index}`)) // TODO how to describe infinitely nested arrays?
            .flatten()
            .map(({ child, key }) => {
                if (!child || isImpossibleChild(child)) { return undefined; }

                if (isLabel(child)) { return <MultiListbox.Label key={key} {...child.props}>{child.props.children}</MultiListbox.Label> as MultiListboxChild<TValue>; }

                if (isGroup<TValue>(child)) {
                    const
                        stats = { selectedCount: 0 },
                        selected = isParentSelected || !!this.props.groupSelectionEnabled && this.props.value!.includes(child.props.id),
                        subchildren = this._renderChildren(child.props.children, stats, selected);

                    statsToFill.selectedCount += subchildren.length ? stats.selectedCount : +selected;

                    return <MultiListbox.Group key={key} {...child.props}>{subchildren}</MultiListbox.Group> as MultiListboxChild<TValue>;
                }

                if (isOption<TValue>(child)) {
                    const { summary, children, ...mlbProps } = child.props;

                    statsToFill.selectedCount += +isParentSelected || +this.props.value!.includes(child.props.value);

                    return <MultiListbox.Option key={key} {...mlbProps}>{children}</MultiListbox.Option> as MultiListboxChild<TValue>;
                }

                return assertNever(child);
            });
    }

    render() {
        const
            { message, placeholder, clearable, opened, pending, disabled, className, onOpen, onChange, onClose, children, ...mlbProps } = this.props,
            stats: { selectedCount: number } = { selectedCount: 0 },
            mlbChildren = this._renderChildren(children, stats),
            { selectedCount } = stats,
            title = (selectedCount ? message! : placeholder!) || '';

        return (
            <DropPanel
                opened={this.state.opened}
                className={`select ${className}`}
                disabled={disabled || !mlbChildren.length}
                title={
                    !pending ?
                        <span className={selectedCount ? 'content' : 'placeholder'}>
                            {
                                isResource(title) ?
                                    <FormattedResource resource={title} values={{ count: selectedCount }} /> :
                                    title
                            }
                        </span> :
                        <div className="skeleton text" />
                }
                icon={!disabled && selectedCount && clearable ? <span className="fa fa-times" onClick={this._onClear} /> : DropPanel.AUTO_ICON}
                snap={[SnapSide.LEFT, SnapSide.RIGHT]}
                closeMode={CloseMode.OUTSIDE}
                onOpen={this._onOpen}
                onClose={this._onClose}>
                {
                    !pending ?
                        <MultiListbox {...mlbProps} onChange={this._onChange}>{mlbChildren}</MultiListbox> :
                        <Spinner config={{ scale: 0.5 }} />
                }
            </DropPanel >
        );
    }
}